"use client";

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { Plus, Building, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useCostCenters } from "@/hooks/useCostCenters";
import { CostCenterStatus } from "@/types/cost-center";
import { supabase } from "@/integrations/supabase/client";

// Schema simplificado para cadastro rápido de centro de custo
const quickAddCostCenterSchema = z.object({
  name: z.string().min(2, "Nome deve ter pelo menos 2 caracteres").max(100, "Nome deve ter no máximo 100 caracteres"),
  description: z.string().optional(),
});

type QuickAddCostCenterSchema = z.infer<typeof quickAddCostCenterSchema>;

interface QuickAddCostCenterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCostCenterAdded: (costCenterId: number) => void;
}

export function QuickAddCostCenterModal({ isOpen, onClose, onCostCenterAdded }: QuickAddCostCenterModalProps) {
  const [isSaving, setIsSaving] = useState(false);
  const { createCostCenter } = useCostCenters();

  const form = useForm<QuickAddCostCenterSchema>({
    resolver: zodResolver(quickAddCostCenterSchema),
    defaultValues: {
      name: "",
      description: "",
    },
  });

  const handleSubmit = async (data: QuickAddCostCenterSchema) => {
    setIsSaving(true);

    try {
      // Obter o usuário atual
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        toast.error("Usuário não autenticado");
        return;
      }

      // Criar centro de custo com dados simplificados
      const costCenterData = {
        name: data.name,
        description: data.description || "",
        status: CostCenterStatus.ACTIVE,
        parent_id: null,
        code: null,
        budgeted_cost: null,
      };

      const newCostCenter = await createCostCenter({
        formData: costCenterData,
        userId: userData.user.id
      });

      // Passar o ID do centro de custo criado para o formulário principal
      onCostCenterAdded(newCostCenter.id);

      // Resetar formulário e fechar modal
      form.reset();
      onClose();

      toast.success("Centro de custo adicionado com sucesso!");
    } catch (error: any) {
      console.error("Erro ao criar centro de custo:", error);
      toast.error(error.message || "Erro ao criar centro de custo");
    } finally {
      setIsSaving(false);
    }
  };

  // Auto-focus no nome quando o modal abre
  React.useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const nameInput = document.getElementById('cost-center-name');
        if (nameInput) {
          nameInput.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building className="h-5 w-5" />
            Adicionar Novo Centro de Custo
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome do Centro de Custo *</FormLabel>
                  <FormControl>
                    <Input
                      id="cost-center-name"
                      placeholder="Digite o nome do centro de custo"
                      {...field}
                      disabled={isSaving}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Descreva o propósito deste centro de custo (opcional)"
                      rows={3}
                      {...field}
                      disabled={isSaving}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Centro de Custo
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}